import { StoryblokLink } from '../types/StoryblokLink';
import { formatStoryblokLink } from '../utils/formatStoryblokLink';
import { Typography } from '@mui/material';
import Link from './base/Link';
import { MenuLinkFooter } from './MenuLinkFooter';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LinkedInIcon } from '../assets/social/linkedin.svg';
import { ReactComponent as FacebookIcon } from '../assets/social/facebook.svg';
import { ReactComponent as TwitterIcon } from '../assets/social/twitter.svg';
import { ReactComponent as InstagramIcon } from '../assets/social/instagram.svg';

type LinkType = StoryblokLink;

interface FooterContent {
  component: string;
  title: string;
  facebook: LinkType;
  twitter: LinkType;
  linkedin: LinkType;
  instagram: LinkType;
  additional_links: { label: string; url: LinkType }[];
  address: string;
  phone_number: string;
  partner_logo?: { filename: string; alt?: string };
  partner_logo_link?: LinkType;
}

interface FooterProps {
  config?: {
    footer_menu: FooterContent[];
  };
}

const Footer = ({ config }: FooterProps) => {
  const { t } = useTranslation();
  if (!config?.footer_menu) {
    return null;
  }

  const footerContent = config.footer_menu.find(
    (item: any) => item.component === 'footer_content',
  ) as FooterContent;

  if (!footerContent) {
    console.error('footer_content not found');
    return null;
  }

  const {
    title,
    facebook: facebookLink,
    twitter: twitterLink,
    linkedin: linkedinLink,
    instagram: instagramLink,
    address,
    phone_number,
    partner_logo,
    partner_logo_link,
  } = footerContent;

  const partnerHref = formatStoryblokLink(partner_logo_link);

  const additionalLinksArray = config.footer_menu.filter(
    (item: any) => item.component === 'additional_links',
  );

  return (
    <footer
      className='bg-overlay-menu-leistungen h-[370px] tablet:h-[331px] pt-[45px] tablet:pt-[74px] pb-[24px] flex flex-col justify-between relative'
      aria-labelledby='footer-heading'
    >
      <div className='max-w-container container mx-auto'>
        <div className='flex flex-col tablet:flex-row  tablet:justify-between gap-2 tablet:gap-10'>
          <Typography
            component='div'
            sx={{
              fontFamily: 'Respira Black',
              color: '#C6C6C6',
              fontSize: {
                xs: '40px', // Mobile
                md: '60px', // Desktop
              },
            }}
          >
            {title}
          </Typography>

          <div className='flex tablet:flex-row tablet:items-start tablet:gap-x-12'>
            {/* Tablet/Desktop: logo + disclaimer stacked */}
            {partner_logo?.filename && (
              <div className='hidden tablet:flex flex-col items-start gap-2'>
                <a href={partnerHref} className='flex items-center'>
                  <img
                    src={partner_logo.filename}
                    alt={partner_logo.alt || 'Partner logo'}
                    className='h-[46px] w-auto'
                  />
                </a>
                <span className='desktop:hidden max-w-[200px] text-[8px] leading-tight' style={{ color: '#C6C6C6' }}>
                  {t('amazon_disclaimer')}
                </span>
              </div>
            )}
            <div className='flex flex-col text-left'>
              {additionalLinksArray?.map((nestedBlok: any, index: number) => (
                <MenuLinkFooter
                  index={index}
                  blok={nestedBlok}
                  key={nestedBlok._uid}
                />
              ))}
              {/* Mobile: logo under links */}
              {partner_logo?.filename && (
                <a
                  href={partnerHref}
                  className='tablet:hidden mt-4 inline-flex items-center'
                >
                  <img
                    src={partner_logo.filename}
                    alt={partner_logo.alt || 'Partner logo'}
                    className='h-auto max-h-[36px] max-w-[140px]'
                  />
                </a>
              )}
              {/* Mobile: disclaimer under logo */}
              <span className='tablet:hidden mt-2 max-w-[200px] text-[8px] leading-tight' style={{ color: '#C6C6C6' }}>
                {t('amazon_disclaimer')}
              </span>
            </div>
          </div>
        </div>
        <div className='flex items-center gap-[64px] absolute bottom-4'>
          <div className='flex justify-center items-center gap-8'>
            {facebookLink?.cached_url && (
              <Link
                href={facebookLink.cached_url}
                target='_blank'
                icon={<FacebookIcon />}
              />
            )}

            {twitterLink?.cached_url && (
              <Link
                href={twitterLink.cached_url}
                target='_blank'
                icon={<TwitterIcon />}
              />
            )}

            {linkedinLink?.cached_url && (
              <Link
                href={linkedinLink.cached_url}
                target='_blank'
                icon={<LinkedInIcon />}
              />
            )}

            {instagramLink?.cached_url && (
              <Link
                href={instagramLink.cached_url}
                target='_blank'
                icon={<InstagramIcon />}
              />
            )}
          </div>
          <Typography
            component='div'
            className='hidden tablet:flex'
            variant='body2'
            sx={{
              color: '#C6C6C6',
              gap: '20px',
              flexWrap: 'wrap',
              alignItems: 'center',
              alignContent: 'center',
            }}
          >
            <span className='style-4-mobile tablet:style-4'>{address}</span>
            <a
              href={`tel:${phone_number}`}
              className='style-4-mobile tablet:style-4'
            >
              {phone_number}
            </a>
          </Typography>
          <Typography
            component='div'
            className='hidden desktop:flex'
            variant='body2'
            sx={{
              color: '#C6C6C6',
              gap: '20px',
              flexWrap: 'wrap',
              alignItems: 'center',
              alignContent: 'center',
            }}
          >
            <span className='max-w-[500px] style-4-mobile'>
              {t('amazon_disclaimer')}
            </span>
          </Typography>
        </div>

        <div className='absolute right-4 bottom-4 h-full flex items-center rotate-180 tablet:hidden z-10'>
          <Typography
            component='div'
            variant='body2'
            sx={{
              color: '#C6C6C6',
              writingMode: 'vertical-lr',
              textOrientation: 'mixed',
              fontFamily: 'Söhne Mono',
              marginBottom: '150px',
              fontSize: {
                xs: '10px', // Mobile
                md: '14px', // Desktop
              },
            }}
          >
            <div>
              <span className='style-4-mobile phone:style-4 text-[8px] phone:text-[8px]'>
                {address}
              </span>
            </div>
            <div>
              <a
                href={`tel:${phone_number}`}
                className='style-4-mobile phone:style-4 text-[8px] phone:text-[8px]'
              >
                {phone_number}
              </a>
            </div>
          </Typography>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
